import { TZDate } from '@date-fns/tz'
import { format } from 'date-fns'
import {
  ARTICLE_TYPE,
  NO_TIME_SLOT_VALUE,
  PHOTO_TYPE,
  POST_TYPE_POSTED,
  PostType,
  STATUS_TYPE,
  VIDEO_TYPE
} from 'interfaces'
import { LEGACY_BUCKET_NAMES_MAP } from 'services/plan/adapter'
import { getFileThumbUrl } from 'services/uploads/util'
import {
  BRAND_BLUESKY,
  BRAND_THREADS,
  BRAND_YOUTUBE,
  FB_POST_TYPE_REEL,
  INPUT_ACCEPT_PHOTO,
  ContentType,
  BRAND_FACEBOOK,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_PINTEREST,
  BRAND_TWITTER,
  BRAND_GOOGLE,
  BRAND_TIKTOK
} from 'shared'
import { PostDestinationType } from 'shared/types'
import { decodeHtml } from 'utils/browser'
import { MS_IN_SEC } from 'utils/constants'
import { INPUT_ACCEPT_VIDEO } from 'utils/file/constants'
import numberToHuman from 'utils/format/numberToHuman'
import toCamelCase from 'utils/toCamelCase'
import { AutoPostSlot } from '../state'


const BASE_TEN = 10
const PREVIEW_PROCESSING_KEYWORD = 'waiting_processing'

export const LEGACY_TYPE_MAP: { [legacyType: string]: ContentType } = {
  photo: PHOTO_TYPE,
  link: ARTICLE_TYPE,
  video: VIDEO_TYPE,
  status: STATUS_TYPE
}

const POST_NETWORK_MAP: { [network: string]: PostDestinationType } = {
  [BRAND_FACEBOOK]: BRAND_FACEBOOK,
  fb: BRAND_FACEBOOK,
  [BRAND_TWITTER]: BRAND_TWITTER,
  tw: BRAND_TWITTER,
  [BRAND_LINKEDIN]: BRAND_LINKEDIN,
  ig: BRAND_INSTAGRAM,
  instagram_direct: BRAND_INSTAGRAM, // eslint-disable-line camelcase
  [BRAND_INSTAGRAM]: BRAND_INSTAGRAM,
  [BRAND_PINTEREST]: BRAND_PINTEREST,
  pi: BRAND_PINTEREST,
  [BRAND_TIKTOK]: BRAND_TIKTOK,
  [BRAND_GOOGLE]: BRAND_GOOGLE,
  [BRAND_YOUTUBE]: BRAND_YOUTUBE,
  [BRAND_THREADS]: BRAND_THREADS,
  [BRAND_BLUESKY]: BRAND_BLUESKY
}

function formatStats(stats: string | null) {
  if (stats === null) {
    return 0
  }

  const count = parseInt(stats, BASE_TEN)
  if (Number.isNaN(count)) {
    return '--'
  }

  return numberToHuman(count)
}

function getPostStats(post: any) {
  const stats: any = {
    likes: parseInt(post.likes, BASE_TEN),
    likesFormatted: formatStats(post.likes),
    shares: parseInt(post.shares, BASE_TEN),
    sharesFormatted: formatStats(post.shares),
    comments: parseInt(post.comments, BASE_TEN),
    commentsFormatted: formatStats(post.comments),
    clicks: parseInt(post.clicks, BASE_TEN),
    clicksFormatted: formatStats(post.clicks)
  }
  if (POST_NETWORK_MAP[post.networkType] === BRAND_PINTEREST) {
    const data = JSON.parse(post.insightsDetails || null)
    stats.clicks = data?.pin_click || post.clicks || 0
    stats.clicksFormatted = formatStats(stats.clicks.toString())
    stats.saves = data?.save || 0
    stats.savesFormatted = formatStats(stats.saves.toString())
    stats.impressions = data?.impression || 0
    stats.impressionsFormatted = formatStats(stats.impressions.toString())
    stats.isRepin = Boolean(data?.is_repin)
  }
  return stats
}

function getNativeVideoUrl(contentType: ContentType, fileExtension: string, post: any) {
  if (contentType !== VIDEO_TYPE || !fileExtension) {
    return undefined
  }
  if (INPUT_ACCEPT_VIDEO.includes(fileExtension) || post.file?.includes('licdn')) {
    return post.file || post.linkUrl
  }
  return undefined
}

export function postAdapter<T>(post: any, index: number): any {
  const contentType = LEGACY_TYPE_MAP[post.typeText]
  const network = POST_NETWORK_MAP[post.networkType]
  let specialType
  const fileUrl = post.file
  const fileExtension = '.' + post.file?.split(/\#|\?/)[0].split('.').pop().toLowerCase()
  const images: string[] = []
  if (contentType === PHOTO_TYPE) {
    const files = post.files ? JSON.parse(post.files) : []
    images.push(...files)
    if (post.file && !images.includes(post.file)) {
      images.unshift(post.file)
    }
  } else if (contentType === ARTICLE_TYPE || contentType === VIDEO_TYPE) {
    if (post.linkImage) {
      images.push(post.linkImage)
    } else if (contentType === ARTICLE_TYPE && post.file) {
      images.push(post.file)
    }
    if (INPUT_ACCEPT_PHOTO.includes(fileExtension)) {
      images.push(fileUrl)
    }
  }

  const nativeVideoUrl = getNativeVideoUrl(contentType, fileExtension, post)

  let details: any
  try {
    details = toCamelCase(JSON.parse(post.details))
  } catch (e) {
    details = undefined
  }

  // EXPL: Map IG location to profile
  if (details && details.locations) {
    details.location = details.locations.find((loc: any) => loc.ppPageId === post.ppPageId.toString())
  }
  // EXPL: `videoThumbnail` only flags that the video has an auto-generated poster frame, so the
  // thumbnail URL has to be derived from the video URL. A `videoThumbnailUrl` / `coverUrl` already
  // present in the details is a cover the user picked explicitly - never overwrite it.
  if (details?.videoThumbnail && nativeVideoUrl && !details.videoThumbnailUrl && !details.coverUrl) {
    details.videoThumbnailUrl = getFileThumbUrl(nativeVideoUrl, 'videos')
  }

  if (
    network !== BRAND_GOOGLE // Do not apply special types for GMB posts
    && (details?.igPostType || (details?.postType && details.postType !== 'post'))
  ) {
    const value = details?.igPostType || details?.postType
    details.postType = value
    specialType = value
  } else if (details?.textBackground) {
    specialType = 'tobi'
  } else if (details?.documentUrl) {
    specialType = 'document'
  }
  if (post.isExternal && !specialType && network === BRAND_FACEBOOK && post.linkUrl && post.linkUrl.includes('/reel/')) {
    specialType = FB_POST_TYPE_REEL
  }

  const isEmpty = post.id === 'empty'
  const now = Date.now()
  const buckets = Array.isArray(post.postTypes) ? post.postTypes : []
  const linkTitle = decodeHtml(post.linkTitle || '')
  let attributes: any = network === BRAND_PINTEREST ? {
    title: linkTitle,
    description: post.linkDescription,
    url: post.linkUrl,
    pinterestBoardUrl: details?.photoAlbum?.url
  } : null
  if (network === BRAND_YOUTUBE) {
    attributes = {
      title: post.status,
      description: post.description
    }
  }
  if (network === BRAND_TIKTOK) {
    attributes = {
      title: post.title,
      description: post.description
    }
  }

  const baseTime = post.baseTime ? parseInt(post.baseTime, BASE_TEN) * MS_IN_SEC : null
  const timestamp = parseInt(post.time, BASE_TEN)
  const time = timestamp * MS_IN_SEC

  let recycle: Date | number | boolean = post.reQueue?.toString() === '1'
  if (recycle) {
    if (post.recycleUntil) {
      recycle = new Date(post.recycleUntil)
    } else if (post.recycleTimes) {
      recycle = parseInt(post.recycleTimes, BASE_TEN)
    }
  }

  const repeatUntil = post.repeatUntil || 'forever'

  let networkLink = post.realLink
  let networkId = null

  try {
    const networkData = JSON.parse(post.networkResponse || '{}')
    networkId = networkData.id
    if (!networkLink && network === BRAND_LINKEDIN && post.statusIdV3) {
      networkLink = `https://www.linkedin.com/feed/update/${post.statusIdV3}`
    } else if (networkId && !networkLink && network === BRAND_YOUTUBE) {
      networkLink = `https://www.youtube.com/watch?v=${networkId}`
    } else if (network === BRAND_BLUESKY && networkData.uri) {
      const bsPostId = networkData.uri.split('/').pop()
      networkLink = `https://bsky.app/profile/${post.name}/post/${bsPostId}`
    }
  } catch (_e) {
    console.log('Invalid network response', post.networkResponse)
  }

  const timeString = post.isVirtual ? post.timeString : post.baseTimeString || post.timeString

  return {
    ...post,
    ppPageId: post.ppPageId?.toString(),
    createdAt: post.firstCreatedAt || post.createdAt,
    weekday: post.weekday || NO_TIME_SLOT_VALUE,
    network,
    attributes,
    id: isEmpty ? `empty-${index}-${now}` : post.postId.toString(),
    isEmpty,
    contentType,
    specialType,
    recycle,
    images: images.filter(Boolean),
    profilePictureUrl: post.picture,
    profileId: post.networkIdV3,
    time,
    baseTime,
    timezone: post.timeTimezone,
    twPostType: post.twPostType,
    repeatUntil,
    status: post.status || '',
    dateString: post.date,
    calendarDate: time ? format(new TZDate(time, post.timeTimezone), 'LL/dd/yy') : '',
    hoursLocal: time ? parseInt(format(new TZDate(time, post.timeTimezone), 'H'), 10) : 0,
    linkTitle,
    linkDescription: decodeHtml(post.linkDescription || ''),
    timeString: post.date === 'Unknown' ? NO_TIME_SLOT_VALUE : timeString,
    nativeVideoUrl,
    fbVideoUrl: contentType === VIDEO_TYPE && post.linkDomain === 'facebook.com' && post.linkUrl,
    buckets: buckets.map((b: string) => LEGACY_BUCKET_NAMES_MAP[b]),
    originalTweetUrl: post.attachmentUrl,
    processingPreview: post.linkTitle === PREVIEW_PROCESSING_KEYWORD
      || post.linkDescription === PREVIEW_PROCESSING_KEYWORD
      || post.linkImage === PREVIEW_PROCESSING_KEYWORD
      || post.linkImage === 'autodetect',
    details,
    realLink: networkLink,
    isFavorite: post.isFavorited === 1,
    bucketId: post.bucketId?.toString(),
    isPending: !post.time,
    postComments: (post.postComments || []).map((c: any) => ({
      ...c,
      publishedAt: c.publishedAt ? c.publishedAt * MS_IN_SEC : undefined,
      status: c.status || 'pending',
      errorMessage: c.error || c.errorMessage
    }))
  } as T
}

export function postedPostAdapter(post: any, index: number, archived?: boolean): any {
  const stats = getPostStats(post)
  const data = postAdapter(post, index)

  return {
    ...data,
    ...stats,
    isArchived: Boolean(archived),
    isExternal: Boolean(post.isExternal),
    isPosted: true
  }
}

export function postsResponseAdapter<T>(response: { posts: any[] }, type?: PostType, archived?: boolean): T[] {
  if (type === POST_TYPE_POSTED) {
    return response.posts.map((p, i) => postedPostAdapter(p, i, archived)) as T[]
  }

  return response.posts.map(postAdapter) as T[]
}

export function autoPostSlotAdapter(slot: any): AutoPostSlot {
  const timestamp = parseInt(slot.time, BASE_TEN)
  const time = timestamp * MS_IN_SEC

  return {
    id: `${slot.postId || slot.id}-${time}`,
    postId: slot.postId.toString(),
    time,
    ppPageId: slot.ppPageId.toString(),
    bucketId: slot.bucketId?.toString(),
    dateString: format(time, 'dd.LLL.yy hh:mm a').toString(),
    timeString: format(new TZDate(time, slot.timeTimezone), 'h:mm a'),
    weekday: slot.weekday || NO_TIME_SLOT_VALUE,
    hoursLocal: parseInt(format(new TZDate(time, slot.timeTimezone), 'H'), 10)
  }
}
